/**
 * Agent-Friendly Task Modal
 * Enhanced Dev Task creation/editing with scope linking, agent instructions,
 * technical context, and Emergent prompt generation.
 */

import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Badge } from './ui/badge';
import { 
  Copy, Check, Sparkles, Link2, Code, TestTube, FileText, 
  Bot, AlertCircle, CheckCircle2, Plus, X, Wand2
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const AgentTaskModal = ({ 
  open, 
  onOpenChange, 
  task = null, // null for new task, object for edit
  onSave,
  getHeaders 
}) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [scopeItems, setScopeItems] = useState([]);
  const [selectedScope, setSelectedScope] = useState(null);
  const [copiedPrompt, setCopiedPrompt] = useState(false);
  const [generatingPrompt, setGeneratingPrompt] = useState(false);
  
  // Form state with all new agent-friendly fields
  const [form, setForm] = useState({
    // Overview
    title: '',
    description: '',
    status: 'pending',
    priority: 'medium',
    assignee: '',
    due_date: '',
    task_type: 'feature', // feature, bug_fix, testing, upgrade, other
    
    // Scope Link
    scope_item_id: '',
    scope_item_name: '',
    
    // Agent Instructions
    agent_instructions: '',
    agent_dos: [],
    agent_donts: [],
    skills_required: [],
    
    // Technical Context
    files_to_modify: [],
    api_endpoints: [],
    db_collections: [],
    dependencies: [],
    
    // Success Criteria
    success_criteria: [],
    test_commands: [],
    verification_steps: [],
    
    // Generated Prompt
    emergent_prompt: ''
  });

  // Input states for array fields
  const [newDo, setNewDo] = useState('');
  const [newDont, setNewDont] = useState('');
  const [newSkill, setNewSkill] = useState('');
  const [newFile, setNewFile] = useState('');
  const [newEndpoint, setNewEndpoint] = useState('');
  const [newCollection, setNewCollection] = useState('');
  const [newCriteria, setNewCriteria] = useState('');
  const [newTestCmd, setNewTestCmd] = useState('');
  const [newVerification, setNewVerification] = useState('');

  // Load scope items for linking
  useEffect(() => {
    if (open) {
      fetchScopeItems();
      if (task) {
        // Edit mode - populate form
        setForm({
          title: task.title || '',
          description: task.description || '',
          status: task.status || 'pending',
          priority: task.priority || 'medium',
          assignee: task.assignee || '',
          due_date: task.due_date || '',
          task_type: task.task_type || 'feature',
          scope_item_id: task.scope_item_id || '',
          scope_item_name: task.scope_item_name || '',
          agent_instructions: task.agent_instructions || '',
          agent_dos: task.agent_dos || [],
          agent_donts: task.agent_donts || [],
          skills_required: task.skills_required || [],
          files_to_modify: task.files_to_modify || [],
          api_endpoints: task.api_endpoints || [],
          db_collections: task.db_collections || [],
          dependencies: task.dependencies || [],
          success_criteria: task.success_criteria || [],
          test_commands: task.test_commands || [],
          verification_steps: task.verification_steps || [],
          emergent_prompt: task.emergent_prompt || ''
        });
        if (task.scope_item_id) {
          fetchScopeDetails(task.scope_item_id);
        }
      } else {
        // New task - reset form
        resetForm();
      }
    }
  }, [open, task]);

  const resetForm = () => {
    setForm({
      title: '',
      description: '',
      status: 'pending',
      priority: 'medium',
      assignee: '',
      due_date: '',
      task_type: 'feature',
      scope_item_id: '',
      scope_item_name: '',
      agent_instructions: '',
      agent_dos: [],
      agent_donts: [],
      skills_required: [],
      files_to_modify: [],
      api_endpoints: [],
      db_collections: [],
      dependencies: [],
      success_criteria: [],
      test_commands: [],
      verification_steps: [],
      emergent_prompt: ''
    });
    setSelectedScope(null);
    setActiveTab('overview');
  };

  const fetchScopeItems = async () => {
    try {
      const response = await axios.get(`${API}/governance/five-pillars/scope`, { headers: getHeaders() });
      const allItems = [...(response.data.mvp_items || []), ...(response.data.future_items || [])];
      setScopeItems(allItems);
    } catch (error) {
      console.error('Error fetching scope items:', error);
    }
  };

  const fetchScopeDetails = async (scopeId) => {
    try {
      const response = await axios.get(`${API}/governance/five-pillars/scope/${scopeId}`, { headers: getHeaders() });
      setSelectedScope(response.data);
    } catch (error) {
      console.error('Error fetching scope details:', error);
    }
  };

  const handleScopeSelect = async (scopeId) => {
    const scopeItem = scopeItems.find(s => s.id === scopeId);
    if (scopeItem) {
      setForm(prev => ({
        ...prev,
        scope_item_id: scopeId,
        scope_item_name: scopeItem.name,
        // Auto-populate from scope if empty
        description: prev.description || scopeItem.description || '',
        files_to_modify: prev.files_to_modify.length > 0 ? prev.files_to_modify : (scopeItem.related_files || []),
      }));
      await fetchScopeDetails(scopeId);
    }
  };

  // Array field handlers
  const addArrayItem = (field, value, setter) => {
    if (value.trim()) {
      setForm(prev => ({
        ...prev,
        [field]: [...prev[field], value.trim()]
      }));
      setter('');
    }
  };

  const removeArrayItem = (field, index) => {
    setForm(prev => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index)
    }));
  };

  // Generate Emergent Prompt
  const generatePrompt = () => {
    setGeneratingPrompt(true);
    
    let prompt = `## Task: ${form.title}\n\n`;
    
    // Description
    if (form.description) {
      prompt += `### Description\n${form.description}\n\n`;
    }
    
    // Linked Scope Details
    if (selectedScope) {
      prompt += `### Feature Context (from Scope: ${selectedScope.name})\n`;
      if (selectedScope.acceptance_criteria) {
        prompt += `**Acceptance Criteria:**\n${selectedScope.acceptance_criteria}\n\n`;
      }
      if (selectedScope.technical_requirements) {
        prompt += `**Technical Requirements:**\n${selectedScope.technical_requirements}\n\n`;
      }
      if (selectedScope.testing_instructions) {
        prompt += `**Testing Instructions:**\n${selectedScope.testing_instructions}\n\n`;
      }
    }
    
    // Agent Instructions
    if (form.agent_instructions) {
      prompt += `### Agent Instructions\n${form.agent_instructions}\n\n`;
    }
    
    // Do's and Don'ts
    if (form.agent_dos.length > 0 || form.agent_donts.length > 0) {
      prompt += `### Guidelines\n`;
      if (form.agent_dos.length > 0) {
        prompt += `**DO:**\n${form.agent_dos.map(d => `- ${d}`).join('\n')}\n\n`;
      }
      if (form.agent_donts.length > 0) {
        prompt += `**DON'T:**\n${form.agent_donts.map(d => `- ${d}`).join('\n')}\n\n`;
      }
    }
    
    // Technical Context
    if (form.files_to_modify.length > 0 || form.api_endpoints.length > 0 || form.db_collections.length > 0) {
      prompt += `### Technical Context\n`;
      if (form.files_to_modify.length > 0) {
        prompt += `**Files to Modify:**\n${form.files_to_modify.map(f => `- \`${f}\``).join('\n')}\n\n`;
      }
      if (form.api_endpoints.length > 0) {
        prompt += `**API Endpoints:**\n${form.api_endpoints.map(e => `- \`${e}\``).join('\n')}\n\n`;
      }
      if (form.db_collections.length > 0) {
        prompt += `**Database Collections:**\n${form.db_collections.map(c => `- \`${c}\``).join('\n')}\n\n`;
      }
    }
    
    // Success Criteria
    if (form.success_criteria.length > 0) {
      prompt += `### Success Criteria\n${form.success_criteria.map(c => `- [ ] ${c}`).join('\n')}\n\n`;
    }
    
    // Test Commands
    if (form.test_commands.length > 0) {
      prompt += `### Test Commands\n\`\`\`bash\n${form.test_commands.join('\n')}\n\`\`\`\n\n`;
    }
    
    // Verification Steps
    if (form.verification_steps.length > 0) {
      prompt += `### Verification Steps\n${form.verification_steps.map((v, i) => `${i + 1}. ${v}`).join('\n')}\n\n`;
    }
    
    prompt += `---\n*Task ID: ${task?.id || 'NEW'} | Priority: ${form.priority} | Assignee: ${form.assignee || 'Unassigned'}*`;
    
    setForm(prev => ({ ...prev, emergent_prompt: prompt }));
    setGeneratingPrompt(false);
    setActiveTab('prompt');
  };

  const copyPrompt = async () => {
    try {
      await navigator.clipboard.writeText(form.emergent_prompt);
      setCopiedPrompt(true);
      toast.success('Prompt copied to clipboard!');
      setTimeout(() => setCopiedPrompt(false), 2000);
    } catch (error) {
      toast.error('Failed to copy prompt');
    }
  };

  const handleSave = async () => {
    if (!form.title.trim()) {
      toast.error('Task title is required');
      return;
    }
    
    try {
      await onSave(form, task?.id);
      onOpenChange(false);
      resetForm();
    } catch (error) {
      console.error('Error saving task:', error);
      toast.error('Failed to save task');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Bot className="h-5 w-5 text-purple-500" />
            {task ? 'Edit Agent Task' : 'Create Agent Task'}
            {form.scope_item_name && (
              <Badge variant="outline" className="ml-2">
                <Link2 className="h-3 w-3 mr-1" />
                {form.scope_item_name}
              </Badge>
            )}
          </DialogTitle>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 overflow-hidden flex flex-col">
          <TabsList className="grid grid-cols-6 w-full">
            <TabsTrigger value="overview" className="text-xs">
              <FileText className="h-3 w-3 mr-1" />
              Overview
            </TabsTrigger>
            <TabsTrigger value="scope" className="text-xs">
              <Link2 className="h-3 w-3 mr-1" />
              Scope Link
            </TabsTrigger>
            <TabsTrigger value="agent" className="text-xs">
              <Bot className="h-3 w-3 mr-1" />
              Agent
            </TabsTrigger>
            <TabsTrigger value="technical" className="text-xs">
              <Code className="h-3 w-3 mr-1" />
              Technical
            </TabsTrigger>
            <TabsTrigger value="completion" className="text-xs">
              <CheckCircle2 className="h-3 w-3 mr-1" />
              Completion
            </TabsTrigger>
            <TabsTrigger value="prompt" className="text-xs">
              <Sparkles className="h-3 w-3 mr-1" />
              Prompt
            </TabsTrigger>
          </TabsList>

          <div className="flex-1 overflow-y-auto p-1">
            {/* Overview Tab */}
            <TabsContent value="overview" className="space-y-4 mt-4">
              <div>
                <Label>Task Title *</Label>
                <Input
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  placeholder="What needs to be done?"
                />
              </div>
              
              <div>
                <Label>Description</Label>
                <Textarea
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder="Detailed description of the task..."
                  rows={4}
                />
              </div>
              
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>Task Type</Label>
                  <Select value={form.task_type} onValueChange={(v) => setForm({ ...form, task_type: v })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="feature">Feature Build</SelectItem>
                      <SelectItem value="bug_fix">Bug Fix</SelectItem>
                      <SelectItem value="testing">Testing</SelectItem>
                      <SelectItem value="upgrade">Upgrade/Refactor</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label>Priority</Label>
                  <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Low</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                      <SelectItem value="critical">Critical</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label>Status</Label>
                  <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="in_progress">In Progress</SelectItem>
                      <SelectItem value="blocked">Blocked</SelectItem>
                      <SelectItem value="completed">Completed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Assignee</Label>
                  <Select value={form.assignee} onValueChange={(v) => setForm({ ...form, assignee: v })}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select agent..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="SAGE">SAGE</SelectItem>
                      <SelectItem value="MoltBot">MoltBot</SelectItem>
                      <SelectItem value="Emergent">Emergent Agent</SelectItem>
                      <SelectItem value="Manual">Manual (Human)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label>Due Date</Label>
                  <Input
                    type="date"
                    value={form.due_date}
                    onChange={(e) => setForm({ ...form, due_date: e.target.value })}
                  />
                </div>
              </div>
            </TabsContent>

            {/* Scope Link Tab */}
            <TabsContent value="scope" className="space-y-4 mt-4">
              <div>
                <Label>Link to Scope Item</Label>
                <Select value={form.scope_item_id} onValueChange={handleScopeSelect}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a scope feature to link..." />
                  </SelectTrigger>
                  <SelectContent>
                    {scopeItems.map(item => (
                      <SelectItem key={item.id} value={item.id}>
                        {item.name} ({item.hub || 'No Hub'})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-gray-500 mt-1">
                  Linking pulls in technical requirements, acceptance criteria, and testing instructions
                </p>
              </div>
              
              {selectedScope && (
                <div className="space-y-4 p-4 bg-slate-800/50 rounded-lg border border-slate-700">
                  <div className="flex items-center justify-between">
                    <h4 className="font-medium text-purple-400">{selectedScope.name}</h4>
                    <Badge>{selectedScope.status}</Badge>
                  </div>
                  
                  {selectedScope.description && (
                    <div>
                      <Label className="text-xs text-gray-400">Description</Label>
                      <p className="text-sm text-gray-300">{selectedScope.description}</p>
                    </div>
                  )}
                  
                  {selectedScope.acceptance_criteria && (
                    <div>
                      <Label className="text-xs text-gray-400">Acceptance Criteria</Label>
                      <p className="text-sm text-gray-300 whitespace-pre-wrap">{selectedScope.acceptance_criteria}</p>
                    </div>
                  )}
                  
                  {selectedScope.technical_requirements && (
                    <div>
                      <Label className="text-xs text-gray-400">Technical Requirements</Label>
                      <p className="text-sm text-gray-300 whitespace-pre-wrap">{selectedScope.technical_requirements}</p>
                    </div>
                  )}
                  
                  {selectedScope.testing_instructions && (
                    <div>
                      <Label className="text-xs text-gray-400">Testing Instructions</Label>
                      <p className="text-sm text-gray-300 whitespace-pre-wrap">{selectedScope.testing_instructions}</p>
                    </div>
                  )}
                  
                  {selectedScope.related_files && selectedScope.related_files.length > 0 && (
                    <div>
                      <Label className="text-xs text-gray-400">Related Files</Label>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {selectedScope.related_files.map((file, i) => (
                          <Badge key={i} variant="outline" className="text-xs font-mono">{file}</Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </TabsContent>

            {/* Agent Instructions Tab */}
            <TabsContent value="agent" className="space-y-4 mt-4">
              <div>
                <Label>Agent Instructions</Label>
                <Textarea
                  value={form.agent_instructions}
                  onChange={(e) => setForm({ ...form, agent_instructions: e.target.value })}
                  placeholder="Specific guidance for the AI agent...&#10;Example: Use the existing CustomerCard component pattern. Follow REST conventions in /routes/. Ensure all endpoints have proper error handling."
                  rows={5}
                />
              </div>
              
              {/* Do's */}
              <div>
                <Label className="text-green-400">DO's (Best Practices)</Label>
                <div className="flex gap-2 mt-1">
                  <Input
                    value={newDo}
                    onChange={(e) => setNewDo(e.target.value)}
                    placeholder="Add a best practice..."
                    onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addArrayItem('agent_dos', newDo, setNewDo))}
                  />
                  <Button size="sm" onClick={() => addArrayItem('agent_dos', newDo, setNewDo)}>
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
                <div className="flex flex-wrap gap-1 mt-2">
                  {form.agent_dos.map((item, i) => (
                    <Badge key={i} className="bg-green-900/50 text-green-300">
                      {item}
                      <X className="h-3 w-3 ml-1 cursor-pointer" onClick={() => removeArrayItem('agent_dos', i)} />
                    </Badge>
                  ))}
                </div>
              </div>
              
              {/* Don'ts */}
              <div>
                <Label className="text-red-400">DON'Ts (Avoid)</Label>
                <div className="flex gap-2 mt-1">
                  <Input
                    value={newDont}
                    onChange={(e) => setNewDont(e.target.value)}
                    placeholder="Add something to avoid..."
                    onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addArrayItem('agent_donts', newDont, setNewDont))}
                  />
                  <Button size="sm" onClick={() => addArrayItem('agent_donts', newDont, setNewDont)}>
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
                <div className="flex flex-wrap gap-1 mt-2">
                  {form.agent_donts.map((item, i) => (
                    <Badge key={i} className="bg-red-900/50 text-red-300">
                      {item}
                      <X className="h-3 w-3 ml-1 cursor-pointer" onClick={() => removeArrayItem('agent_donts', i)} />
                    </Badge>
                  ))}
                </div>
              </div>
              
              {/* Skills Required */}
              <div>
                <Label className="text-blue-400">Skills/Knowledge Required</Label>
                <div className="flex gap-2 mt-1">
                  <Input
                    value={newSkill}
                    onChange={(e) => setNewSkill(e.target.value)}
                    placeholder="e.g., React, FastAPI, MongoDB..."
                    onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addArrayItem('skills_required', newSkill, setNewSkill))}
                  />
                  <Button size="sm" onClick={() => addArrayItem('skills_required', newSkill, setNewSkill)}>
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
                <div className="flex flex-wrap gap-1 mt-2">
                  {form.skills_required.map((item, i) => (
                    <Badge key={i} className="bg-blue-900/50 text-blue-300">
                      {item}
                      <X className="h-3 w-3 ml-1 cursor-pointer" onClick={() => removeArrayItem('skills_required', i)} />
                    </Badge>
                  ))}
                </div>
              </div>
            </TabsContent>

            {/* Technical Context Tab */}
            <TabsContent value="technical" className="space-y-4 mt-4">
              {/* Files to Modify */}
              <div>
                <Label>Files to Modify</Label>
                <div className="flex gap-2 mt-1">
                  <Input
                    value={newFile}
                    onChange={(e) => setNewFile(e.target.value)}
                    placeholder="/app/backend/routes/example.py"
                    className="font-mono text-sm"
                    onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addArrayItem('files_to_modify', newFile, setNewFile))}
                  />
                  <Button size="sm" onClick={() => addArrayItem('files_to_modify', newFile, setNewFile)}>
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
                <div className="flex flex-wrap gap-1 mt-2">
                  {form.files_to_modify.map((item, i) => (
                    <Badge key={i} variant="outline" className="font-mono text-xs">
                      {item}
                      <X className="h-3 w-3 ml-1 cursor-pointer" onClick={() => removeArrayItem('files_to_modify', i)} />
                    </Badge>
                  ))}
                </div>
              </div>
              
              {/* API Endpoints */}
              <div>
                <Label>API Endpoints Involved</Label>
                <div className="flex gap-2 mt-1">
                  <Input
                    value={newEndpoint}
                    onChange={(e) => setNewEndpoint(e.target.value)}
                    placeholder="GET /api/example"
                    className="font-mono text-sm"
                    onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addArrayItem('api_endpoints', newEndpoint, setNewEndpoint))}
                  />
                  <Button size="sm" onClick={() => addArrayItem('api_endpoints', newEndpoint, setNewEndpoint)}>
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
                <div className="flex flex-wrap gap-1 mt-2">
                  {form.api_endpoints.map((item, i) => (
                    <Badge key={i} variant="outline" className="font-mono text-xs bg-purple-900/30">
                      {item}
                      <X className="h-3 w-3 ml-1 cursor-pointer" onClick={() => removeArrayItem('api_endpoints', i)} />
                    </Badge>
                  ))}
                </div>
              </div>
              
              {/* DB Collections */}
              <div>
                <Label>Database Collections</Label>
                <div className="flex gap-2 mt-1">
                  <Input
                    value={newCollection}
                    onChange={(e) => setNewCollection(e.target.value)}
                    placeholder="users, jobs, scope_items..."
                    className="font-mono text-sm"
                    onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addArrayItem('db_collections', newCollection, setNewCollection))}
                  />
                  <Button size="sm" onClick={() => addArrayItem('db_collections', newCollection, setNewCollection)}>
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
                <div className="flex flex-wrap gap-1 mt-2">
                  {form.db_collections.map((item, i) => (
                    <Badge key={i} variant="outline" className="font-mono text-xs bg-orange-900/30">
                      {item}
                      <X className="h-3 w-3 ml-1 cursor-pointer" onClick={() => removeArrayItem('db_collections', i)} />
                    </Badge>
                  ))}
                </div>
              </div>
            </TabsContent>

            {/* Completion Tab */}
            <TabsContent value="completion" className="space-y-4 mt-4">
              {/* Success Criteria */}
              <div>
                <Label>Success Criteria (How agent knows it's done)</Label>
                <div className="flex gap-2 mt-1">
                  <Input
                    value={newCriteria}
                    onChange={(e) => setNewCriteria(e.target.value)}
                    placeholder="e.g., API returns 200 with correct data"
                    onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addArrayItem('success_criteria', newCriteria, setNewCriteria))}
                  />
                  <Button size="sm" onClick={() => addArrayItem('success_criteria', newCriteria, setNewCriteria)}>
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
                <div className="space-y-1 mt-2">
                  {form.success_criteria.map((item, i) => (
                    <div key={i} className="flex items-center gap-2 p-2 bg-slate-800/50 rounded">
                      <CheckCircle2 className="h-4 w-4 text-green-500" />
                      <span className="flex-1 text-sm">{item}</span>
                      <X className="h-4 w-4 cursor-pointer text-gray-500 hover:text-red-400" onClick={() => removeArrayItem('success_criteria', i)} />
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Test Commands */}
              <div>
                <Label>Test Commands</Label>
                <div className="flex gap-2 mt-1">
                  <Input
                    value={newTestCmd}
                    onChange={(e) => setNewTestCmd(e.target.value)}
                    placeholder='curl -X GET "$API/endpoint"'
                    className="font-mono text-sm"
                    onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addArrayItem('test_commands', newTestCmd, setNewTestCmd))}
                  />
                  <Button size="sm" onClick={() => addArrayItem('test_commands', newTestCmd, setNewTestCmd)}>
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
                <div className="space-y-1 mt-2">
                  {form.test_commands.map((item, i) => (
                    <div key={i} className="flex items-center gap-2 p-2 bg-slate-900 rounded font-mono text-xs">
                      <TestTube className="h-4 w-4 text-purple-500" />
                      <span className="flex-1">{item}</span>
                      <X className="h-4 w-4 cursor-pointer text-gray-500 hover:text-red-400" onClick={() => removeArrayItem('test_commands', i)} />
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Verification Steps */}
              <div>
                <Label>Verification Steps</Label>
                <div className="flex gap-2 mt-1">
                  <Input
                    value={newVerification}
                    onChange={(e) => setNewVerification(e.target.value)}
                    placeholder="e.g., Navigate to /settings and verify form appears"
                    onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addArrayItem('verification_steps', newVerification, setNewVerification))}
                  />
                  <Button size="sm" onClick={() => addArrayItem('verification_steps', newVerification, setNewVerification)}>
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
                <div className="space-y-1 mt-2">
                  {form.verification_steps.map((item, i) => (
                    <div key={i} className="flex items-center gap-2 p-2 bg-slate-800/50 rounded">
                      <span className="text-xs text-gray-500 w-5">{i + 1}.</span>
                      <span className="flex-1 text-sm">{item}</span>
                      <X className="h-4 w-4 cursor-pointer text-gray-500 hover:text-red-400" onClick={() => removeArrayItem('verification_steps', i)} />
                    </div>
                  ))}
                </div>
              </div>
            </TabsContent>

            {/* Prompt Tab */}
            <TabsContent value="prompt" className="space-y-4 mt-4">
              <div className="flex items-center justify-between">
                <Label>Emergent Prompt</Label>
                <Button 
                  onClick={generatePrompt} 
                  disabled={generatingPrompt}
                  size="sm"
                  className="bg-purple-600 hover:bg-purple-700"
                >
                  <Wand2 className="h-4 w-4 mr-1" />
                  {generatingPrompt ? 'Generating...' : 'Generate Prompt'}
                </Button>
              </div>
              
              <Textarea
                value={form.emergent_prompt}
                onChange={(e) => setForm({ ...form, emergent_prompt: e.target.value })}
                placeholder="Click 'Generate Prompt' to create an agent-ready prompt from all task details, or write your own..."
                rows={15}
                className="font-mono text-sm"
              />
              
              {form.emergent_prompt && (
                <Button 
                  onClick={copyPrompt} 
                  className="w-full"
                  variant={copiedPrompt ? "default" : "outline"}
                >
                  {copiedPrompt ? (
                    <>
                      <Check className="h-4 w-4 mr-2" />
                      Copied!
                    </>
                  ) : (
                    <>
                      <Copy className="h-4 w-4 mr-2" />
                      Copy Prompt to Clipboard
                    </>
                  )}
                </Button>
              )}
              
              <p className="text-xs text-gray-500">
                This prompt combines all task details into a format optimized for Emergent agents. 
                Copy and paste directly into Emergent to start work.
              </p>
            </TabsContent>
          </div>
        </Tabs>

        <DialogFooter className="border-t border-slate-700 pt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSave} className="bg-blue-600 hover:bg-blue-700">
            {task ? 'Update Task' : 'Create Task'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AgentTaskModal;
